/**
 * Strategic encounters (M4 spec §10.6, plan I4; seams S-M2-8, S-M2-9):
 * - `createStrategicEncounters(inner, view, tuning)` wraps M3's `EncounterPort` (M2's director after the bridge):
 *   the inner danger scale (the layout's regional factor) is raised by `dangerGain ×` the strategic
 *   `SystemEconomyView.danger` (0..1) when the view knows the system, and the interdiction rate drops by
 *   `raiderShare` for each local raider agent (the Reach's raiders are the encounters, so the director does not
 *   double them). Live danger (TR-6): the first term of the rate is also scaled by `1 + dangerRateGain ×` the
 *   same strategic danger (0..1; no change when unknown); the blockade term is not. With no strategic danger, no
 *   raiders and `dangerRateGain` 0 (shipped) it is the inner port unchanged.
 * - M5 blockades (S-M3-11, plan C15): in the blockaded local system with a war band hostile to the player, the
 *   blockade source (`charter/blockade.ts`, the live one by default) adds its rate to the interdiction rate and its
 *   band is the interdictor group.
 * - `spawnManifestWrecks` turns the manifest's off-screen wrecks into M2 combat wrecks once each; M9 CH-3b: a disabled
 *   record (`claim` ≥ 1) becomes a hulk keyed by its `ow-` id instead, which goes when its record leaves the manifest
 *   or (a disabled one, not an open claim) expires in the system (`expireManifestHulks`). Scale SC-5e1: a record whose
 *   strategic position (the encounter node + U(0, `wreckOffsetM`), `strategic/combat.json`) lies inside a station's
 *   hull materialises outside it (`WreckKeepOut`, R + `wreckClearM` from the centre); the record keeps its position.
 *
 * Message rate (the director asks per encounter roll; wrecks arrive with a manifest).
 */
import { liveBlockadeSource, type BlockadeSource } from '../charter/blockade'
import type { Quat, Vec3 } from '../core/math'
import type { EncounterPort } from '../reach/ports'
import type { SalvageRuntime } from '../space/contracts'
import { WRECK_CLAIM, type OffscreenWreck } from '../strategic/types'
import { STEP_KIND, type ContactStore } from './contacts'

/** What the wrapper reads of the strategic side. */
export interface StrategicEncounterView {
  /** The strategic danger 0..1 of `systemIndex`, or null when unknown (not the viewed system). */
  danger(systemIndex: number): number | null
  /** Local raider agents in space now. */
  localRaiders(): number
}

export interface StrategicEncounterTuning {
  /** Danger scale multiplier per unit of strategic danger: `inner × (1 + dangerGain × danger)`. */
  dangerGain: number
  /** Each local raider agent removes this share of the interdiction rate (to zero at 1/share raiders). */
  raiderShare: number
  /**
   * Interdiction rate multiplier per unit of live strategic danger (TR-6):
   * `inner × max(0, 1 − raiders × raiderShare) × (1 + dangerRateGain × danger) + blockade`. 0 or absent: no effect.
   */
  dangerRateGain?: number
}

export function createStrategicEncounters(
  inner: EncounterPort,
  view: StrategicEncounterView,
  tuning: StrategicEncounterTuning,
  blockade: BlockadeSource | null = liveBlockadeSource,
): EncounterPort {
  return {
    dangerScale(systemIndex: number): number {
      const d = view.danger(systemIndex)
      const base = inner.dangerScale(systemIndex)
      return d === null ? base : base * (1 + tuning.dangerGain * d)
    },
    interdictionRatePerMin(systemIndex: number): number {
      const k = 1 - view.localRaiders() * tuning.raiderShare
      const extra = blockade === null ? 0 : blockade.extraRatePerMin(systemIndex)
      const base = inner.interdictionRatePerMin(systemIndex) * (k > 0 ? k : 0)
      const gain = tuning.dangerRateGain ?? 0
      const d = gain === 0 ? null : view.danger(systemIndex)
      return (d === null ? base : base * (1 + gain * d)) + extra
    },
    interdictorGroup: (systemIndex, rng) => blockade?.group(systemIndex) ?? inner.interdictorGroup(systemIndex, rng),
  }
}

/** Local raider agents in space (not docked). */
export function countLocalRaiders(s: ContactStore): number {
  let n = 0
  for (let i = 0; i < s.count; i++) if (s.role[i] === 'raider' && s.kind[i] !== STEP_KIND.docked) n++
  return n
}

/**
 * Scale SC-5e1: moves `pos` (in place) out of every station's bounding sphere to R + a clearance from its centre;
 * true when it moved. `traffic/bays.ts createStationKeepOut` over M2's stations runtime.
 */
export type WreckKeepOut = (pos: Vec3) => boolean

const wreckPos: Vec3 = { x: 0, y: 0, z: 0 }
const wreckVel: Vec3 = { x: 0, y: 0, z: 0 }
const wreckRot: Quat = { x: 0, y: 0, z: 0, w: 1 }

/**
 * M9 CH-3b: the hulks the manifest's disabled records spawned, keyed by their `ow-` ids. A record that leaves the
 * manifest (claimed or gone by the next one) or a disabled record that expires while the player is in the system
 * despawns its hulk; an open claim's record never expires here (MS-1).
 */
export interface ManifestHulks {
  /** The `ow-` ids of the manifest's live hulks. */
  readonly keys: Set<string>
  /** The last manifest's records (their claim and expiry). */
  records: readonly OffscreenWreck[]
  /** The earliest `expiresAt` of a live disabled hulk (strategic seconds); +Infinity = none. */
  nextExpiry: number
}

const NO_RECORDS: readonly OffscreenWreck[] = []

export function createManifestHulks(): ManifestHulks {
  return { keys: new Set(), records: NO_RECORDS, nextExpiry: Number.POSITIVE_INFINITY }
}

/** A disabled or open-claim record: it surfaces as a hulk, not a timed combat wreck. */
export function isHulkRecord(w: OffscreenWreck): boolean {
  return w.claim !== undefined && w.claim >= WRECK_CLAIM.disabled
}

/** Hulk records expire unless an open claim holds them (MS-1). */
function expires(w: OffscreenWreck): boolean {
  return w.claim === WRECK_CLAIM.disabled
}

function nextHulkExpiry(h: ManifestHulks): number {
  let t = Number.POSITIVE_INFINITY
  const recs = h.records
  for (let i = 0; i < recs.length; i++) {
    const w = recs[i]
    if (expires(w) && w.expiresAt < t && h.keys.has(w.id)) t = w.expiresAt
  }
  return t
}

/** True when `wrecks` holds hulk record `id`. */
function hasHulkRecord(wrecks: readonly OffscreenWreck[], id: string): boolean {
  for (let i = 0; i < wrecks.length; i++) if (wrecks[i].id === id) return isHulkRecord(wrecks[i])
  return false
}

/**
 * Spawns each off-screen wreck not yet in `spawned` as an M2 combat wreck of its hull's ship def. Returns how many
 * spawned. Wrecks of unknown hulls, or when the pool is full, are skipped (and retried with the next manifest). A
 * wreck's `maker` (MC-f5c; absent for raider, guardian and corp losses) goes to its parts (M8 MC-f5b).
 *
 * M9 CH-3b: a disabled or open-claim record (`claim` ≥ 1) spawns a hulk (`spawnHulk`, at rest, keyed by its `ow-`
 * id) instead, never a combat wreck; a disabled one already expired at `now` is skipped. `spawnHulk` is idempotent,
 * so every manifest re-asserts its hulks (a load that cleared the pool gets them back); `hulks` counts each once.
 * The manifest's previous hulks whose records are gone are despawned first. Allocates nothing per record (the
 * scratch pose is shared); a new hulk key is a rare event.
 *
 * Scale SC-5e1: with `keepOut` a wreck or hulk whose strategic position lies inside a station's hull spawns outside
 * it (R + clearance from the centre, radially); the strategic record is not changed, so no strategic pin moves.
 */
export function spawnManifestWrecks(
  salvage: Pick<SalvageRuntime, 'spawnCombatWreck' | 'spawnHulk' | 'despawnHulk'>,
  wrecks: readonly OffscreenWreck[],
  defIndexOfHull: (hull: string) => number,
  spawned: Set<string>,
  hulks: ManifestHulks,
  now: number,
  keepOut: WreckKeepOut | null = null,
): number {
  // Every live key came from the last manifest's records, so walking them (not the set) allocates no iterator.
  const prev = hulks.records
  for (let i = 0; i < prev.length && hulks.keys.size > 0; i++) {
    const id = prev[i].id
    if (!hulks.keys.has(id) || hasHulkRecord(wrecks, id)) continue
    salvage.despawnHulk(id)
    hulks.keys.delete(id)
  }
  hulks.records = wrecks
  let n = 0
  for (let i = 0; i < wrecks.length; i++) {
    const w = wrecks[i]
    const hulk = isHulkRecord(w)
    if (!hulk && spawned.has(w.id)) continue
    if (hulk && expires(w) && w.expiresAt <= now) continue
    const def = defIndexOfHull(w.hull)
    if (def < 0) continue
    wreckPos.x = w.pos[0]
    wreckPos.y = w.pos[1]
    wreckPos.z = w.pos[2]
    if (keepOut !== null) keepOut(wreckPos)
    if (hulk) {
      // PG-2 (R2-4): an open claim's hulk counts outside `hulks.maxLocal` (re-asserted here when a claim opens or closes).
      if (salvage.spawnHulk(def, wreckPos, wreckRot, w.id, w.claim === WRECK_CLAIM.open) < 0 || hulks.keys.has(w.id)) continue
      hulks.keys.add(w.id)
      n++
      continue
    }
    if (salvage.spawnCombatWreck(def, wreckPos, wreckVel, wreckRot, w.seed, w.maker ?? null) < 0) continue
    spawned.add(w.id)
    n++
  }
  hulks.nextExpiry = nextHulkExpiry(hulks)
  return n
}

/**
 * M9 CH-3b: despawns the manifest hulks whose disabled records expired by `now` (strategic seconds; an open claim
 * never expires here). Returns how many went. Per tick: allocation-free, and a no-op before the earliest expiry.
 */
export function expireManifestHulks(salvage: Pick<SalvageRuntime, 'despawnHulk'>, hulks: ManifestHulks, now: number): number {
  if (now < hulks.nextExpiry) return 0
  let n = 0
  const recs = hulks.records
  for (let i = 0; i < recs.length; i++) {
    const w = recs[i]
    if (!expires(w) || w.expiresAt > now || !hulks.keys.delete(w.id)) continue
    salvage.despawnHulk(w.id)
    n++
  }
  hulks.nextExpiry = nextHulkExpiry(hulks)
  return n
}

/** M9 CH-3b: the system was left: every manifest hulk goes (with `salvage` null, only the bookkeeping is cleared). */
export function clearManifestHulks(salvage: Pick<SalvageRuntime, 'despawnHulk'> | null, hulks: ManifestHulks): void {
  const recs = hulks.records
  if (salvage !== null) for (let i = 0; i < recs.length; i++) if (hulks.keys.has(recs[i].id)) salvage.despawnHulk(recs[i].id)
  hulks.keys.clear()
  hulks.records = NO_RECORDS
  hulks.nextExpiry = Number.POSITIVE_INFINITY
}
